'use strict';

/**
 * RichDocEditor — plansheet's rich markdown editor. It is pict-section-markdowneditor
 * (CodeMirror 6, segmented, with a pict-section-content preview that understands
 * KaTeX math, Mermaid diagrams, and embedded images/drawings) subclassed to:
 *   - route pasted / dragged / phone-camera images through Parime (onImageUpload),
 *   - add an "Insert drawing" toolbar button that opens the Excalidraw editor, and
 *   - notify the DocEditor host on every change so it can debounce-autosave.
 *
 * It is hosted inside PictView-DocEditor (which owns the title/type/save chrome)
 * and mounts into #DocEditor-EditorHost, bound to AppData.RichDocEditor.Segments.
 */

const libMarkdownEditor = require('pict-section-markdowneditor');

function buildConfiguration()
{
	let tmpConfig = JSON.parse(JSON.stringify(libMarkdownEditor.default_configuration));
	tmpConfig.ViewIdentifier = 'Plansheet-RichDocEditor';
	tmpConfig.DefaultDestinationAddress = '#DocEditor-EditorHost';
	tmpConfig.TargetElementAddress = '#DocEditor-EditorHost';
	tmpConfig.ContentDataAddress = 'AppData.RichDocEditor.Segments';
	tmpConfig.DefaultPreviewMode = 'side';
	tmpConfig.EnableRichPreview = true;
	tmpConfig.AutoRender = false;

	if (Array.isArray(tmpConfig.Renderables))
	{
		for (let i = 0; i < tmpConfig.Renderables.length; i++)
		{
			tmpConfig.Renderables[i].DestinationAddress = '#DocEditor-EditorHost';
		}
	}

	// Add a drawing button to the per-segment top-right toolbar (after the
	// built-in bold/italic/code/heading/link/image actions).
	tmpConfig.ButtonsTR = (tmpConfig.ButtonsTR || []).slice();
	tmpConfig.ButtonsTR.push({ HTML: '✎', Action: 'insertDrawing', Class: 'pict-mde-button-drawing', Title: 'Insert a drawing' });

	return tmpConfig;
}

class PictViewRichDocEditor extends libMarkdownEditor
{
	_host() { return this.pict.views['Plansheet-DocEditor']; }
	_ownerContext() { let tmpHost = this._host(); return (tmpHost && typeof tmpHost.ownerContext === 'function') ? tmpHost.ownerContext() : { OwnerType: '', IDOwner: 0 }; }

	/**
	 * Pasted / dragged / picked image -> upload to Parime, owned by the doc's
	 * entity (so it lives in that entity's media gallery), and insert the blob
	 * URL. Returning true = async handling; returning false falls back to the
	 * base64 embed.
	 */
	onImageUpload(pFile, pSegmentIndex, fCallback)
	{
		let tmpOwner = this._ownerContext();
		if (!tmpOwner.IDOwner) { return false; }
		this.pict.providers.MediaAPI.upload(tmpOwner.OwnerType, tmpOwner.IDOwner, pFile)
			.then((pRow) => fCallback(null, this.pict.providers.MediaAPI.blobURL(pRow.IDMedia)))
			.catch((pErr) => fCallback(pErr.message || String(pErr)));
		return true;
	}

	/** Toolbar "Insert drawing" -> open the Excalidraw editor; on save insert it. */
	insertDrawing(pSegmentIndex)
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp.showDrawingEditor !== 'function') { return; }
		let tmpOwner = this._ownerContext();
		tmpApp.showDrawingEditor({ OwnerType: tmpOwner.OwnerType, IDOwner: tmpOwner.IDOwner, IDMedia: 0 }, (pResult) =>
		{
			if (pResult && pResult.URL)
			{
				this._insertImageMarkdown(pSegmentIndex, pResult.URL, pResult.Title || 'drawing');
			}
		});
	}

	onContentChange(pSegmentIndex, pContent)
	{
		let tmpHost = this._host();
		if (tmpHost && typeof tmpHost.onEditorChanged === 'function') { tmpHost.onEditorChanged(); }
	}
}

module.exports = PictViewRichDocEditor;
module.exports.default_configuration = buildConfiguration();
